import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setSentryUser } from '@/lib/sentry';
import { formatHoursMinutes } from "@/lib/video-metadata";
import { Button } from "@/components/ui/button";
import { Plus, List, ListOrdered, Scissors, BookOpen, LogOut, Crown, Clock, Video, Key, History, FileText, ChevronDown, ChevronUp, ChevronRight, Server, Palette, Shield, Users, FolderOpen, Film, Download, Ticket, QrCode, Trophy, Library, Sparkles, FileOutput, Youtube, Mail, Wallet, DollarSign, TrendingUp, MessageCircle, Images, Handshake, GraduationCap, BarChart3, Calendar, Radio, Lightbulb, Bot, Megaphone, ExternalLink } from "lucide-react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { buildBrandsLandingUrl } from '@/lib/brandLeads/brandsLanding';
import { Badge } from "@/components/ui/badge";
import {
  Sidebar as SidebarPrimitive,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useMyDataQuery } from "@/generated/graphql";
import { useQuery } from '@apollo/client';
import { CAMPAIGN_AUDIT_ACCESS, type CampaignAuditAccessData } from '@/queries/campaignAuditAccess';
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useHasFeature } from "@/lib/useHasFeature";
import { SOCIAL_PUBLISHING_FEATURE } from "@/lib/publishing/socialPublishingFeature";
import { isPaidSubscription } from "@/lib/quota/isPaidSubscription";
import { isSubscriptionPastDue } from "@/lib/billing/isSubscriptionPastDue";
import { resolveEarlyRenewalPrompt } from "@/lib/billing/resolveEarlyRenewalPrompt";
import { pendingCycleResetAt } from "@/lib/billing/pendingCycleResetAt";
import { resolvePastDueRecovery } from "@/lib/billing/resolvePastDueRecovery";
import { startStripeCustomerPortal } from "@/lib/billing/startStripeCustomerPortal";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/use-user-role";
import { useHomeRoute } from "@/hooks/use-home-route";
import { TEMPLATES_PATH, isTemplatesNavPath } from "@/lib/brand-kit/templateRoutes";
import PixRenewalModal from "@/components/PixRenewalModal";
import { PlanRenewalDate } from "@/components/billing/PlanRenewalDate";
import { RenewedCycleNotice } from "@/components/billing/RenewedCycleNotice";
import BuyExtraMinutesModal from "@/components/BuyExtraMinutesModal";
import { SidebarWalletCard } from "@/components/SidebarWalletCard";
import { shouldShowEarnWalletLink } from "@/lib/payout/shouldShowEarnWalletLink";
import { classifyQuotaLevel } from "@/lib/quota/deriveQuotaPressure";
import { quotaLevelBarClass } from "@/lib/quota/quotaLevelBarClass";
import { useViralizeQualification } from '@/contexts/viralizeQualificationContext';
import { SuperHeroSidebarBanner } from '@/components/campaign-entry/SuperHeroSidebarBanner';

declare global {
  interface Window {
    posthog?: {
      identify: (distinctId: string, properties?: Record<string, unknown>) => void;
      reset: () => void;
      capture: (eventName: string, properties?: Record<string, unknown>) => void;
    };
    op?: (...args: unknown[]) => void;
    rybbit?: {
      identify: (userId: string | number) => void;
      event: (eventName: string, properties?: Record<string, unknown>) => void;
      clearUserId: () => void;
    };
  }
}

// Sidebar entries that wear the spinning-border attention treatment. Keep this
// set small — the effect stops reading as "look here" once several items spin.
const SPIN_HIGHLIGHT_ACTIONS = new Set(['viralize-course']);

const useMenuItems = (objective?: string | null, hasSocialPublishing = false, profileReady = true) => {
  const { t } = useTranslation();
  const isOtherChannels = objective === "other_channels";
  const isOwnBrand = objective === "own_brand";
  const items = [];
  items.push(
    { title: t('sidebar.createNewProject'), icon: Plus, variant: "gradient" as const, action: "create" as const, proOnly: false },
  );
  if (profileReady) {
    if (isOtherChannels) {
      items.push(
        { title: t('sidebar.exploreLibrary'), icon: Users, variant: "ghost" as const, action: "community-library" as const, proOnly: false },
        { title: t('sidebar.myProjects'), icon: Library, variant: "ghost" as const, action: "library" as const, proOnly: false },
        // "Ideias de template" is intentionally hidden from the sidebar for now —
        // the feature is launching silently. The /template-ideas route and page
        // stay live (reachable by direct URL) so we can reveal it later by
        // restoring this menu item. Admin moderation at
        // /admin/template-suggestions stays visible so submissions that trickle
        // in can still be reviewed.
        // { title: t('sidebar.templateIdeas'), icon: Lightbulb, variant: "ghost" as const, action: "template-ideas" as const, proOnly: false },
      );
    } else {
      items.push(
        { title: t('sidebar.myProjects'), icon: List, variant: "ghost" as const, action: "home" as const, proOnly: false },
        { title: t('sidebar.exploreLibrary'), icon: Users, variant: "ghost" as const, action: "community-library" as const, proOnly: false },
        // "Ideias de template" is intentionally hidden from the sidebar for now —
        // the feature is launching silently. The /template-ideas route and page
        // stay live (reachable by direct URL) so we can reveal it later by
        // restoring this menu item. Admin moderation at
        // /admin/template-suggestions stays visible so submissions that trickle
        // in can still be reviewed.
        // { title: t('sidebar.templateIdeas'), icon: Lightbulb, variant: "ghost" as const, action: "template-ideas" as const, proOnly: false },
      );
      if (isOwnBrand) {
        items.push(
          { title: t('sidebar.hireClippers'), icon: Megaphone, variant: "ghost" as const, action: "hire-clippers" as const, proOnly: false },
        );
      }
    }
  }
  items.push(
    { title: t('sidebar.exportedClips'), icon: Scissors, variant: "ghost" as const, action: "clips" as const, proOnly: false, isNew: false },
  );
  if (hasSocialPublishing) {
    items.push(
      { title: t('sidebar.calendar'), icon: Calendar, variant: "ghost" as const, action: "calendar" as const, proOnly: false, isNew: false },
      { title: t('sidebar.queues'), icon: ListOrdered, variant: "ghost" as const, action: "queues" as const, proOnly: false, isNew: false },
    );
  }
  items.push(
    { title: t('sidebar.viralizeCourse'), icon: GraduationCap, variant: "ghost" as const, action: "viralize-course" as const, proOnly: false, isNew: false },
    { title: t('sidebar.analytics'), icon: BarChart3, variant: "ghost" as const, action: "analytics" as const, proOnly: false, isNew: false },
  );
  if (profileReady && (isOtherChannels || isOwnBrand)) {
    items.push(
      { title: t('sidebar.earn'), icon: DollarSign, variant: "ghost" as const, action: "earn" as const, proOnly: false, isNew: isOtherChannels },
      { title: t('sidebar.earnWallet'), icon: Wallet, variant: "ghost" as const, action: "earn-wallet" as const, proOnly: false, isNew: false },
    );
    if (isOtherChannels) {
      items.push(
        { title: t('sidebar.ranks'), icon: Trophy, variant: "ghost" as const, action: "ranks" as const, proOnly: false, isNew: false },
      );
    }
  }
  // "Indique e Ganhe" is intentionally hidden from the sidebar for now — the
  // affiliate program isn't publicly disclosed yet. The /affiliates route and
  // page stay live (reachable by direct URL) so we can reveal it later by
  // restoring this menu item.
  // items.push(
  //   { title: t('sidebar.affiliates'), icon: UserPlus, variant: "ghost" as const, action: "affiliates" as const, proOnly: false, isNew: true },
  // );
  items.push(
    { title: t('sidebar.brandKit'), icon: Palette, variant: "ghost" as const, action: "branding" as const, proOnly: true, isNew: false },
    { title: t('sidebar.myMedia'), icon: Images, variant: "ghost" as const, action: "my-media" as const, proOnly: false, isNew: false },
    { title: t('sidebar.tutorials'), icon: BookOpen, variant: "ghost" as const, action: "tutorials" as const, proOnly: false, isNew: false },
  );
  return items;
};

const useLibraryMenuItems = () => {
  const { t } = useTranslation();
  return [
    { title: t('sidebar.myLibrary'), icon: Library, variant: "ghost" as const, action: "library" as const },
    { title: t('sidebar.communityLibrary'), icon: Users, variant: "ghost" as const, action: "community-library" as const },
  ];
};

const useApiMenuItems = () => {
  const { t } = useTranslation();
  return [
    { title: t('sidebar.apiKeys'), icon: Key, variant: "ghost" as const, action: "api-keys" as const, proOnly: true },
    { title: t('sidebar.apiRequests'), icon: History, variant: "ghost" as const, action: "api-requests" as const, proOnly: true },
    { title: t('sidebar.apiReference'), icon: FileText, variant: "ghost" as const, action: "api-documentation" as const, proOnly: true },
  ];
};

const useAdminMenuItems = () => {
  const { t } = useTranslation();
  return [
    { title: t('sidebar.admin.resultados'), icon: TrendingUp, variant: "ghost" as const, action: "admin-resultados" as const },
    { title: t('sidebar.admin.botConversations'), icon: MessageCircle, variant: "ghost" as const, action: "admin-bot-conversations" as const },
    { title: t('sidebar.admin.cpmLeads'), icon: MessageCircle, variant: "ghost" as const, action: "admin-cpm-leads" as const },
    { title: t('sidebar.admin.users'), icon: Users, variant: "ghost" as const, action: "admin-users" as const },
    { title: t('sidebar.admin.projects'), icon: FolderOpen, variant: "ghost" as const, action: "admin-projects" as const },
    { title: t('sidebar.admin.clips'), icon: Film, variant: "ghost" as const, action: "admin-clips" as const },
    { title: t('sidebar.admin.downloads'), icon: Download, variant: "ghost" as const, action: "admin-downloads" as const },
    { title: t('sidebar.admin.coupons'), icon: Ticket, variant: "ghost" as const, action: "admin-coupons" as const },
    { title: t('sidebar.admin.liveWatches'), icon: Radio, variant: "ghost" as const, action: "admin-live-watches" as const },
    { title: t('sidebar.admin.emailFlows'), icon: Mail, variant: 'ghost' as const, action: 'admin-email-flows' as const },
    { title: t('sidebar.admin.payouts'), icon: Wallet, variant: "ghost" as const, action: "admin-payouts" as const },
    { title: t('sidebar.admin.pixVerifications'), icon: Shield, variant: "ghost" as const, action: "admin-pix-verifications" as const },
    { title: t('sidebar.admin.courseRevenueShares'), icon: Handshake, variant: "ghost" as const, action: "admin-course-revenue-shares" as const },
    { title: t('sidebar.admin.commissionsAndRoyalties'), icon: DollarSign, variant: "ghost" as const, action: "admin-affiliate-earnings" as const },
    { title: t('sidebar.admin.contatos'), icon: Mail, variant: "ghost" as const, action: "admin-contatos" as const },
    { title: t('sidebar.admin.challenges'), icon: Trophy, variant: "ghost" as const, action: "admin-challenges" as const },
    { title: t('sidebar.admin.campaigns'), icon: Sparkles, variant: "ghost" as const, action: "admin-campaigns" as const },
    { title: t('sidebar.admin.clipperSeasons'), icon: Calendar, variant: "ghost" as const, action: "admin-clipper-seasons" as const },
    { title: t('sidebar.admin.channels'), icon: Youtube, variant: "ghost" as const, action: "admin-channels" as const },
    { title: t('sidebar.admin.library'), icon: Library, variant: "ghost" as const, action: "admin-library" as const },
    { title: t('sidebar.admin.brandKits'), icon: Palette, variant: "ghost" as const, action: "admin-brand-kits" as const },
    { title: t('sidebar.admin.templateSuggestions'), icon: Lightbulb, variant: "ghost" as const, action: "admin-template-suggestions" as const },
    { title: t('sidebar.admin.communityCuration'), icon: Sparkles, variant: "ghost" as const, action: "admin-community-curation" as const },
    { title: t('sidebar.admin.aiUsage'), icon: Bot, variant: "ghost" as const, action: "admin-ai-usage" as const },
  ];
};

const EarnWalletSidebarItem = ({ title, isActive }: { title: string; isActive: boolean }) => {
  return (
    <SidebarMenuItem>
      <SidebarMenuSub>
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild isActive={isActive}>
            <Link to="/earn/wallet">
              <Wallet className="w-4 h-4" />
              <span className="ml-2 text-sm">{title}</span>
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      </SidebarMenuSub>
    </SidebarMenuItem>
  );
};

const Sidebar = () => {
  const { t } = useTranslation();
  const { state, setOpenMobile } = useSidebar();
  const { requestViralizeCourse } = useViralizeQualification();
  const {
    data: myData,
    loading: isMyDataLoading,
    refetch: refetchMyData,
  } = useMyDataQuery();
  const userData = myData?.myData;
  const subscriptionData = userData?.subscription;
  const plan = userData?.subscription?.plan;
  const collapsed = state === "collapsed";
  const isPaidPlan = isPaidSubscription(subscriptionData);
  const isPastDue = isSubscriptionPastDue(subscriptionData);
  const { toast } = useToast();
  const hasProFeatures = useHasFeature('pro_features');
  const hasAPIIntegration = useHasFeature('api_integration');
  const hasSocialPublishing = useHasFeature(SOCIAL_PUBLISHING_FEATURE);
  const { permissions } = useUserRole();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLibraryGroupOpen, setIsLibraryGroupOpen] = useState(false);
  const [isApiGroupOpen, setIsApiGroupOpen] = useState(false);
  const [isAdminGroupOpen, setIsAdminGroupOpen] = useState(false);
  const [isPixRenewalModalOpen, setIsPixRenewalModalOpen] = useState(false);
  const [renewalModalVariant, setRenewalModalVariant] = useState<'pix' | 'grant'>('pix');
  // Paying early because the limit is close: the new limits start on payment.
  const [renewalResetNow, setRenewalResetNow] = useState(false);
  const [isBuyExtraMinutesOpen, setIsBuyExtraMinutesOpen] = useState(false);
  const [payingPastDue, setPayingPastDue] = useState(false);
  const challengeParticipation = userData?.challenge_participation ?? null;
  const { route: homeRoute } = useHomeRoute();
  const profileReady = Boolean(userData);
  const showChromeLoading = isMyDataLoading && !userData;
  const menuItems = useMenuItems(userData?.onboarding_objective, hasSocialPublishing, profileReady);
  const libraryMenuItems = useLibraryMenuItems();
  const apiMenuItems = useApiMenuItems();
  const adminMenuItems = useAdminMenuItems();
  const isAdmin = userData?.is_admin === true;
  const earlyRenewalPath = resolveEarlyRenewalPrompt({
    subscription: subscriptionData,
    objective: userData?.onboarding_objective,
    now: new Date(),
  });
  const renewedCyclePending = pendingCycleResetAt(subscriptionData, new Date()) !== null;

  // Auditor entry: show "Auditoria de Campanha" for users who are listed
  // as campaign_admins on >=1 campaign, including platform admins.
  const { data: auditedData } = useQuery<CampaignAuditAccessData>(CAMPAIGN_AUDIT_ACCESS);
  const auditedCampaignCount = auditedData?.adminCpmCampaigns?.total ?? 0;
  const isCampaignAuditor = auditedCampaignCount > 0;

  useEffect(() => {
    if (!userData) {
      return;
    }

    try {
      const subscription = userData.subscription;
      const plan = subscription?.plan;

      if (window.op) {
        const name = userData.name;
        const splitName = name?.split(' ') || [];
        window.op('identify', {
          profileId: userData.id,
          email: userData.email,
          firstName: splitName[0] || '',
          lastName: splitName[1] || '',
          properties: {
            planName: plan?.name || '',
            currentMonthClips: subscription?.current_month_clips || 0,
            currentMonthProcessingSeconds: subscription?.current_month_processing_seconds || 0,
          },
        })
        window.op('track', 'identified')
      }

      if (window.rybbit) {
        window.rybbit.identify(userData.id)
        window.rybbit.event('authentication', {
          user_id: userData.id,
          email: userData.email,
        })
      }

      // PostHog user identification with plan data
      if (window.posthog) {
        window.posthog.identify(String(userData.id), {
          email: userData.email,
          name: userData.name,
          // Plan information
          plan_name: plan?.name || 'free',
          plan_value: plan?.value || 0,
          plan_max_clips: plan?.max_clips || 0,
          plan_processing_seconds: plan?.processing_seconds || 0,
          // Subscription usage
          current_month_clips: subscription?.current_month_clips || 0,
          current_month_processing_seconds: subscription?.current_month_processing_seconds || 0,
          // Subscription metadata
          payment_provider: subscription?.payment_provider || null,
          is_paid_plan: isPaidSubscription(subscription),
          has_cancellation: Boolean(subscription?.cancellation_at),
          // User metadata
          job_title: userData.job_title || null,
          created_at: userData.createdAt,
        });
      }

      // Sentry user identification
      setSentryUser({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        jobTitle: userData.job_title,
        objective: userData.onboarding_objective || userData.target_objective,
        createdAt: userData.createdAt,
        role: userData.role,
        isAdmin: userData.is_admin,
        subscription,
      });
    } catch (error) {
      // Supressed Error
      console.error(error);
    }
  }, [userData]);

  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handlePastDuePay = async () => {
    const recovery = resolvePastDueRecovery(subscriptionData);
    if (recovery.kind === 'store_manage') {
      window.open(recovery.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (recovery.kind !== 'stripe_portal') {
      setRenewalModalVariant('pix');
      setRenewalResetNow(earlyRenewalPath === 'pix');
      setIsPixRenewalModalOpen(true);
      return;
    }
    try {
      setPayingPastDue(true);
      const url = await startStripeCustomerPortal(window.location.href, {
        preferOpenInvoice: true,
      });
      window.location.href = url;
    } catch (error) {
      console.error('Failed to open Stripe customer portal:', error);
      toast({
        title: t('billing.pastDueBanner'),
        description: t('billing.pastDuePortalError'),
        variant: 'destructive',
      });
    } finally {
      setPayingPastDue(false);
    }
  };

  const getPathForAction = (action: string) => {
    switch (action) {
      case 'challenge':
        return undefined;
      case 'home':
        return '/projects';
      case 'clips':
        return '/clips';
      case 'calendar':
        return '/calendar';
      case 'queues':
        return '/queues';
      case 'viralize-course':
        return '/courses/viralize-em-4-semanas';
      case 'analytics':
        return '/analytics';
      case 'library-group':
      case 'library':
        return '/library';
      case 'community-library':
        return '/community-library';
      case 'template-ideas':
        return '/template-ideas';
      case 'earn':
        return '/earn';
      case 'ranks':
        return '/rankings';
      case 'earn-wallet':
        return '/earn/wallet';
      case 'affiliates':
        return '/affiliates';
      case 'branding':
        return TEMPLATES_PATH;
      case 'my-media':
        return '/my-media';
      case 'api-keys':
        return '/api-keys';
      case 'api-requests':
        return '/api-requests';
      case 'api-documentation':
        return '/api-documentation';
      case 'tutorials':
        return '/tutorials';
      case 'admin-resultados':
        return '/admin/resultados';
      case 'admin-users':
        return '/admin/users';
      case 'admin-bot-conversations':
        return '/admin/bot-conversations';
      case 'admin-cpm-leads':
        return '/admin/cpm-leads';
      case 'admin-projects':
        return '/admin/projects';
      case 'admin-clips':
        return '/admin/clips';
      case 'admin-downloads':
        return '/admin/downloads';
      case 'admin-coupons':
        return '/admin/coupons';
      case 'admin-live-watches':
        return '/admin/live-watches';
      case 'admin-email-flows':
        return '/admin/email-flows';
      case 'admin-payouts':
        return '/admin/payouts';
      case 'admin-pix-verifications':
        return '/admin/pix-verifications';
      case 'admin-course-revenue-shares':
        return '/admin/course-revenue-shares';
      case 'admin-affiliate-earnings':
        return '/admin/affiliate-earnings';
      case 'admin-contatos':
        return '/admin/contatos';
      case 'admin-challenges':
        return '/admin/challenges';
      case 'admin-campaigns':
        return '/admin/campaigns';
      case 'admin-clipper-seasons':
        return '/admin/clipper-seasons';
      case 'admin-channels':
        return '/admin/channels';
      case 'admin-library':
        return '/admin/library';
      case 'admin-brand-kits':
        return '/admin/brand-kit-new';
      case 'admin-template-suggestions':
        return '/admin/template-suggestions';
      case 'admin-community-curation':
        return '/admin/community-curation';
      case 'admin-ai-usage':
        return '/admin/ai-usage';
      default:
        return undefined;
    }
  };

  const isActiveAction = (action: string) => {
    const pathname = location.pathname;
    switch (action) {
      case 'create':
        return homeRoute === '/projects'
          ? (pathname === '/projects' || pathname.startsWith('/project/'))
          : pathname.startsWith(homeRoute);
      case 'challenge':
        return pathname.startsWith('/challenge/');
      case 'home':
        return pathname === '/projects' || pathname.startsWith('/project/');
      case 'clips':
        return pathname.startsWith('/clips');
      case 'calendar':
        return pathname.startsWith('/calendar');
      case 'queues':
        return pathname.startsWith('/queues');
      case 'viralize-course':
        return pathname.startsWith('/courses/viralize-em-4-semanas');
      case 'analytics':
        return pathname.startsWith('/analytics');
      case 'library-group':
        return pathname.startsWith('/library') || pathname.startsWith('/community-library');
      case 'library':
        return pathname.startsWith('/library');
      case 'community-library':
        return pathname.startsWith('/community-library');
      case 'template-ideas':
        return pathname.startsWith('/template-ideas');
      case 'earn':
        return pathname === '/earn';
      case 'ranks':
        return pathname.startsWith('/rankings');
      case 'earn-wallet':
        return pathname.startsWith('/earn/wallet');
      case 'affiliates':
        return pathname.startsWith('/affiliates');
      case 'branding':
        return isTemplatesNavPath(pathname);
      case 'my-media':
        return pathname.startsWith('/my-media');
      case 'api-keys':
        return pathname.startsWith('/api-keys');
      case 'api-requests':
        return pathname.startsWith('/api-requests');
      case 'api-documentation':
        return pathname.startsWith('/api-documentation') || pathname.startsWith('/api-getting-started') || pathname.startsWith('/api-reference') || pathname.startsWith('/api-playground');
      case 'tutorials':
        return pathname.startsWith('/tutorials');
      case 'admin-resultados':
        return pathname.startsWith('/admin/resultados');
      case 'admin-users':
        return pathname.startsWith('/admin/users');
      case 'admin-bot-conversations':
        return pathname.startsWith('/admin/bot-conversations');
      case 'admin-cpm-leads':
        return pathname.startsWith('/admin/cpm-leads');
      case 'admin-projects':
        return pathname.startsWith('/admin/projects');
      case 'admin-clips':
        return pathname.startsWith('/admin/clips');
      case 'admin-downloads':
        return pathname.startsWith('/admin/downloads');
      case 'admin-coupons':
        return pathname.startsWith('/admin/coupons');
      case 'admin-live-watches':
        return pathname.startsWith('/admin/live-watches');
      case 'admin-email-flows':
        return pathname.startsWith('/admin/email-flows');
      case 'admin-payouts':
        return pathname.startsWith('/admin/payouts');
      case 'admin-pix-verifications':
        return pathname.startsWith('/admin/pix-verifications');
      case 'admin-course-revenue-shares':
        return pathname.startsWith('/admin/course-revenue-shares');
      case 'admin-affiliate-earnings':
        return pathname.startsWith('/admin/affiliate-earnings');
      case 'admin-contatos':
        return pathname.startsWith('/admin/contatos');
      case 'admin-challenges':
        return pathname.startsWith('/admin/challenges');
      case 'admin-campaigns':
        return pathname.startsWith('/admin/campaigns');
      case 'admin-clipper-seasons':
        return pathname.startsWith('/admin/clipper-seasons');
      case 'admin-channels':
        return pathname.startsWith('/admin/channels');
      case 'admin-library':
        return pathname.startsWith('/admin/library');
      case 'admin-brand-kits':
        return pathname.startsWith('/admin/brand-kit');
      case 'admin-template-suggestions':
        return pathname.startsWith('/admin/template-suggestions');
      case 'admin-community-curation':
        return pathname.startsWith('/admin/community-curation');
      case 'admin-ai-usage':
        return pathname.startsWith('/admin/ai-usage');
      default:
        return false;
    }
  };

  const isAnyLibraryItemActive = () => {
    return libraryMenuItems.some(item => isActiveAction(item.action));
  };

  const isAnyApiItemActive = () => {
    return apiMenuItems.some(item => isActiveAction(item.action));
  };

  const isAnyAdminItemActive = () => {
    return adminMenuItems.some(item => isActiveAction(item.action));
  };

  return (
    <SidebarPrimitive collapsible="icon">
      <SidebarContent>
        {/* Logo */}
        <div className={`border-b border-border ${collapsed ? 'p-3 flex justify-center' : 'p-3 sm:p-4 lg:p-6'}`}>
          <div id="sidebar-logo" className="flex items-center justify-center gap-2">
            <Link to={homeRoute}>
              {collapsed ? (
                <img src="/images/logo-compact.svg" alt="autoclipper" className="h-14 w-14 object-contain" />
              ) : (
                <img src="/images/autoclipper-logo-full.png" alt="autoclipper" className="h-8" />
              )}
            </Link>
          </div>
        </div>

        <SidebarGroup>
          <SidebarGroupContent>
            <SuperHeroSidebarBanner collapsed={collapsed} previewEligible={isAdmin || isCampaignAuditor} onNavigate={() => setOpenMobile(false)} />
            <SidebarMenu>
              {challengeParticipation && (!challengeParticipation.end_date || new Date(challengeParticipation.end_date) >= new Date()) && (
                <SidebarMenuItem key={`challenge-${challengeParticipation.id}`} className="mb-3 px-1">
                  <div className="challenge-card-wrapper">
                    <Link
                      to={`/challenge/${challengeParticipation.id}`}
                      className="challenge-card-inner"
                    >
                      <Trophy
                        className="challenge-card-trophy w-4 h-4 sm:w-5 sm:h-5"
                        style={{ color: 'hsl(75 100% 55%)' }}
                      />
                      {!collapsed && (
                        <>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span
                              className="text-[10px] font-bold uppercase tracking-widest mb-0.5"
                              style={{ color: 'hsl(180 100% 60%)' }}
                            >
                              {t('sidebar.challengeLabel')}
                            </span>
                            <span
                              className="text-xs sm:text-sm font-semibold leading-tight truncate"
                              style={{ background: 'var(--gradient-primary)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                            >
                              {challengeParticipation.name}
                            </span>
                          </div>
                          <ChevronRight
                            className="challenge-card-chevron w-3.5 h-3.5"
                            style={{ color: 'hsl(180 100% 60%)' }}
                          />
                        </>
                      )}
                    </Link>
                  </div>
                </SidebarMenuItem>
              )}

              {showChromeLoading && (
                <>
                  <SidebarMenuItem>
                    <SidebarMenuSkeleton showIcon />
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuSkeleton showIcon />
                  </SidebarMenuItem>
                </>
              )}

              {menuItems.map((item) => {
                const path = getPathForAction(item.action);
                const isActive = isActiveAction(item.action);
                const isDisabled = item.proOnly && !hasProFeatures;

                // Hide "Create new project" button if user doesn't have permission
                if (item.action === 'create' && !permissions.canCreateProjects) {
                  return null;
                }

                // Earn wallet: render as sub-item under Ganhe com Cortes for clippers.
                if (item.action === 'earn-wallet') {
                  if (collapsed) return null;
                  if (!shouldShowEarnWalletLink({ objective: userData?.onboarding_objective })) {
                    return null;
                  }
                  return <EarnWalletSidebarItem key={item.title} title={item.title} isActive={isActive} />;
                }

                // Library group: render as collapsible with sub-items
                if (item.action === 'library-group') {
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        onClick={() => setIsLibraryGroupOpen(!isLibraryGroupOpen)}
                        isActive={isAnyLibraryItemActive()}
                        className={`w-full justify-start ${collapsed ? 'px-2 sm:px-3' : ''}`}
                      >
                        <item.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                        {!collapsed && (
                          <div className="flex items-center justify-between w-full">
                            <span className="ml-2 text-sm sm:text-base">{item.title}</span>
                            {isLibraryGroupOpen ?
                              <ChevronUp className="w-4 h-4" /> :
                              <ChevronDown className="w-4 h-4" />
                            }
                          </div>
                        )}
                      </SidebarMenuButton>
                      {!collapsed && isLibraryGroupOpen && (
                        <SidebarMenuSub>
                          {libraryMenuItems.map((subItem) => {
                            const subPath = getPathForAction(subItem.action);
                            const subIsActive = isActiveAction(subItem.action);
                            return (
                              <SidebarMenuSubItem key={subItem.title}>
                                <SidebarMenuSubButton asChild isActive={subIsActive}>
                                  <Link to={subPath || '#'}>
                                    <subItem.icon className="w-4 h-4" />
                                    <span className="ml-2">{subItem.title}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      )}
                    </SidebarMenuItem>
                  );
                }

                const buttonContent = (
                  <>
                    <item.icon className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                    {!collapsed && (
                      <div className="ml-2 flex-1 flex items-center justify-between min-w-0">
                        <span className="text-sm sm:text-base truncate min-w-0 flex-shrink">{item.title}</span>
                        {item.proOnly && (
                          <Badge variant="outline" className="ml-1 text-xs text-primary border-primary shrink-0">
                            PRO
                          </Badge>
                        )}
                      </div>
                    )}
                  </>
                );
                return (
                  <SidebarMenuItem key={item.title}>
                    {item.action === 'create' ? (
                      <SidebarMenuButton asChild>
                        <Button
                          variant={isActiveAction('create') ? 'gradient' : 'ghost'}
                          size="lg"
                          className={`w-full justify-start ${collapsed ? 'px-2 sm:px-3' : ''}`}
                          onClick={() => {
                            if (homeRoute === '/community-library') {
                              // CommunityLibraryPage reads `focus` on mount to scroll,
                              // focus and ring-highlight the smart search/upload input —
                              // avoids racing the page's own mount when navigating here
                              // from elsewhere (a plain getElementById right after
                              // navigate() can run before the page has rendered).
                              navigate('/community-library?focus=video-url-input');
                              return;
                            }
                            navigate(homeRoute);
                            const input = document.getElementById('video-url-input');
                            if (input) {
                              input.focus();
                              input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }}
                        >
                          {buttonContent}
                        </Button>
                      </SidebarMenuButton>
                    ) : (
                      <div className={(SPIN_HIGHLIGHT_ACTIONS.has(item.action) || (item.action === 'earn' && userData?.onboarding_objective === 'other_channels')) && !collapsed ? 'spin-border-highlight' : ''}>
                      <SidebarMenuButton asChild isActive={isActive}>
                        {item.action === 'viralize-course' ? (
                          <Button
                            type="button"
                            data-viralize-course-entry
                            disabled={isMyDataLoading}
                            variant={item.variant}
                            size="lg"
                            className={`w-full justify-start ${collapsed ? 'px-2 sm:px-3' : ''} ${isActive ? '!bg-primary/25 !text-primary' : ''}`}
                            onClick={(event) => {
                              if (isMyDataLoading) return;
                              setOpenMobile(false);
                              void requestViralizeCourse(event.currentTarget);
                            }}
                          >
                            {buttonContent}
                          </Button>
                        ) : item.action === 'hire-clippers' ? (
                          // Campaigns are sold on the site: open it pre-filled
                          // with the account's contact, in a new tab.
                          <Button
                            asChild
                            variant={item.variant}
                            size="lg"
                            className={`w-full justify-start ${collapsed ? 'px-2 sm:px-3' : ''}`}
                          >
                            <a
                              href={buildBrandsLandingUrl('sidebar', userData)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setOpenMobile(false)}
                            >
                              {buttonContent}
                              {!collapsed && (
                                <ExternalLink
                                  aria-hidden="true"
                                  className="ml-1 w-3.5 h-3.5 shrink-0 text-muted-foreground"
                                />
                              )}
                            </a>
                          </Button>
                        ) : (
                        <Button
                          asChild={!isDisabled}
                          variant={item.variant}
                          size="lg"
                          disabled={isDisabled}
                          className={`w-full justify-start ${collapsed ? 'px-2 sm:px-3' : ''} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${isActive ? '!bg-primary/25 !text-primary' : ''}`}
                          onClick={isDisabled ? (e) => e.preventDefault() : undefined}
                        >
                          {isDisabled ? (
                            <div className="flex items-center justify-center w-full">
                              {buttonContent}
                            </div>
                          ) : (
                            <Link to={path || '#'}>
                              {buttonContent}
                            </Link>
                          )}
                        </Button>
                        )}
                      </SidebarMenuButton>
                      </div>
                    )}
                  </SidebarMenuItem>
                );
              })}

              {/* API Integration Group */}
              {hasAPIIntegration && <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => {
                    if (!hasProFeatures) return;
                    setIsApiGroupOpen(!isApiGroupOpen);
                  }}
                  isActive={isAnyApiItemActive()}
                  className={`w-full justify-start ${collapsed ? 'px-2 sm:px-3' : ''} ${!hasProFeatures ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!hasProFeatures}
                >
                  <Server className="w-4 h-4 sm:w-5 sm:h-5" />
                  {!collapsed && (
                    <div className="flex items-center justify-between w-full">
                      <span className="ml-2 text-sm sm:text-base">API</span>
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="text-xs text-primary border-primary">
                          PRO
                        </Badge>
                        {hasProFeatures && (
                          isApiGroupOpen ?
                            <ChevronUp className="w-4 h-4" /> :
                            <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                  )}
                </SidebarMenuButton>
                {!collapsed && hasProFeatures && isApiGroupOpen && (
                  <SidebarMenuSub>
                    {apiMenuItems.map((item) => {
                      const path = getPathForAction(item.action);
                      const isActive = isActiveAction(item.action);
                      return (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton asChild isActive={isActive}>
                            <Link to={path || '#'}>
                              <item.icon className="w-4 h-4" />
                              <span className="ml-2">{item.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>}

              {/* Campaign Audit — for any user (incl. admins) assigned as auditors */}
              {isCampaignAuditor && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname.startsWith('/campaign-audit')}
                    className={`w-full justify-start ${collapsed ? 'px-2 sm:px-3' : ''}`}
                  >
                    <Link to="/campaign-audit">
                      <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
                      {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                          <span className="ml-2 text-sm sm:text-base">
                            {t('sidebar.campaignAudit')}
                          </span>
                          <Badge
                            variant="outline"
                            className="text-[10px] text-amber-500 border-amber-500"
                          >
                            {t('sidebar.campaignAuditBadge')}
                          </Badge>
                        </div>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* Admin Group - Only visible to admin users */}
              {isAdmin && <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => setIsAdminGroupOpen(!isAdminGroupOpen)}
                  isActive={isAnyAdminItemActive()}
                  className={`w-full justify-start ${collapsed ? 'px-2 sm:px-3' : ''}`}
                >
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
                  {!collapsed && (
                    <div className="flex items-center justify-between w-full">
                      <span className="ml-2 text-sm sm:text-base">{t('sidebar.admin.title')}</span>
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="text-xs text-red-500 border-red-500">
                          ADMIN
                        </Badge>
                        {isAdminGroupOpen ?
                          <ChevronUp className="w-4 h-4" /> :
                          <ChevronDown className="w-4 h-4" />
                        }
                      </div>
                    </div>
                  )}
                </SidebarMenuButton>
                {!collapsed && isAdminGroupOpen && (
                  <SidebarMenuSub>
                    {adminMenuItems.map((item) => {
                      const path = getPathForAction(item.action);
                      const isActive = isActiveAction(item.action);
                      return (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton asChild isActive={isActive}>
                            <Link to={path || '#'}>
                              <item.icon className="w-4 h-4" />
                              <span className="ml-2">{item.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Cloud Storage */}
        {!collapsed && (showChromeLoading || profileReady) && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {showChromeLoading ? (
                  <SidebarMenuItem>
                    <div
                      data-testid="sidebar-chrome-loading"
                      role="status"
                      aria-label={t('common.loading')}
                      className="flex flex-col gap-3 p-3 sm:p-4 rounded-xl border border-white/20 bg-white/5 mx-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-5 w-14" />
                      </div>
                      <Skeleton className="h-2 w-full" />
                      <Skeleton className="h-2 w-full" />
                      <Skeleton className="h-8 w-full" />
                    </div>
                  </SidebarMenuItem>
                ) : (
                  <>
                <SidebarMenuItem>
                  <div
                    data-testid="sidebar-plan-card"
                    className={`flex flex-col gap-2 p-3 sm:p-4 ${
                      isPastDue
                        ? 'bg-red-600/10 border-red-500'
                        : isPaidPlan
                          ? 'bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20'
                          : 'bg-white/5 border-white/20'
                    } rounded-xl border mx-2`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {isPaidPlan ? (
                        <Crown className={`w-5 h-5 ${isPastDue ? 'text-red-500' : 'text-primary'}`} />
                      ) : null }
                      <div className="flex flex-row items-center justify-between w-full gap-2">
                        <span className={`text-xs sm:text-sm font-medium ${isPastDue ? 'text-red-600' : ''}`}>
                          {t('sidebar.myPlan')}
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            isPastDue
                              ? 'bg-red-600 text-white border-red-600'
                              : isPaidPlan
                                ? 'bg-primary/10 text-primary border-primary/30'
                                : 'bg-white/10 text-white border-white/30'
                          }`}
                        >
                          {plan?.name}
                        </Badge>
                      </div>
                    </div>
                    {isPastDue && (
                      <p className="text-xs font-semibold text-red-600">{t('sidebar.pastDue')}</p>
                    )}
                    <PlanRenewalDate subscription={subscriptionData} className="-mt-1" />
                    <div className="space-y-3">
                    {userData?.onboarding_objective === 'other_channels' ? (
                      <>
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="flex items-center gap-1">
                              <Sparkles className="w-4 h-4" />
                              {t('sidebar.curations')}
                            </span>
                            <span className="font-medium">
                              {subscriptionData?.current_month_library_curations || 0} / {(subscriptionData?.plan?.max_library_curations || 0) >= 1000000 ? t('common.unlimited') : subscriptionData?.plan?.max_library_curations || 0}
                            </span>
                          </div>
                          {(subscriptionData?.plan?.max_library_curations || 0) > 0 && (subscriptionData?.plan?.max_library_curations || 0) < 1000000 && (
                            <Progress
                              value={(subscriptionData?.current_month_library_curations || 0) / subscriptionData.plan.max_library_curations * 100}
                              className="h-2"
                              indicatorClassName={quotaLevelBarClass(classifyQuotaLevel({ kind: 'countable', used: subscriptionData?.current_month_library_curations || 0, max: subscriptionData?.plan?.max_library_curations || 0 }))}
                            />
                          )}
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="flex items-center gap-1">
                              <FileOutput className="w-4 h-4" />
                              {t('sidebar.clips')}
                            </span>
                            <span className="font-medium">
                              {subscriptionData?.current_month_library_exports || 0} / {(subscriptionData?.plan?.max_library_exports || 0) >= 1000000 ? t('common.unlimited') : subscriptionData?.plan?.max_library_exports || 0}
                            </span>
                          </div>
                          {(subscriptionData?.plan?.max_library_exports || 0) > 0 && (subscriptionData?.plan?.max_library_exports || 0) < 1000000 && (
                            <Progress
                              value={(subscriptionData?.current_month_library_exports || 0) / subscriptionData.plan.max_library_exports * 100}
                              className="h-2"
                              indicatorClassName={quotaLevelBarClass(classifyQuotaLevel({ kind: 'countable', used: subscriptionData?.current_month_library_exports || 0, max: subscriptionData?.plan?.max_library_exports || 0 }))}
                            />
                          )}
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {t('sidebar.time')}
                            </span>
                            <span className="font-medium">
                              {formatHoursMinutes(subscriptionData?.current_month_library_video_seconds || 0)} / {formatHoursMinutes(subscriptionData?.plan?.max_library_video_seconds || 0)}
                            </span>
                          </div>
                          <Progress
                            value={subscriptionData?.current_month_library_video_seconds && subscriptionData?.plan?.max_library_video_seconds
                              ? (subscriptionData.current_month_library_video_seconds / subscriptionData.plan.max_library_video_seconds) * 100
                              : 0}
                            className="w-full h-2"
                            indicatorClassName={quotaLevelBarClass(classifyQuotaLevel({ kind: 'time', used: subscriptionData?.current_month_library_video_seconds || 0, max: subscriptionData?.plan?.max_library_video_seconds || 0 }))}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {t('sidebar.time')}
                            </span>
                            <span className="font-medium">
                              {formatHoursMinutes(subscriptionData?.current_month_processing_seconds || 0)} / {formatHoursMinutes(subscriptionData?.plan?.processing_seconds || 36000)}
                            </span>
                          </div>
                          <Progress
                            value={subscriptionData?.current_month_processing_seconds && subscriptionData?.plan?.processing_seconds
                              ? (subscriptionData.current_month_processing_seconds / subscriptionData.plan.processing_seconds) * 100
                              : 0}
                            className="w-full h-2"
                            indicatorClassName={quotaLevelBarClass(classifyQuotaLevel({ kind: 'time', used: subscriptionData?.current_month_processing_seconds || 0, max: subscriptionData?.plan?.processing_seconds || 0 }))}
                          />
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="flex items-center gap-1">
                              <Video className="w-4 h-4" />
                              {t('sidebar.clips')}
                            </span>
                            <span className="font-medium">
                              {subscriptionData?.current_month_clips || 0} / {(subscriptionData?.plan?.max_clips || 0) >= 1000000 ? t('common.unlimited') : subscriptionData?.plan?.max_clips || 100}
                            </span>
                          </div>
                          <Progress
                            value={subscriptionData?.current_month_clips && subscriptionData?.plan?.max_clips
                              ? (subscriptionData.current_month_clips / subscriptionData.plan.max_clips) * 100
                              : 0}
                            className="h-2"
                            indicatorClassName={quotaLevelBarClass(classifyQuotaLevel({ kind: 'countable', used: subscriptionData?.current_month_clips || 0, max: subscriptionData?.plan?.max_clips || 0 }))}
                          />
                        </div>
                      </>
                    )}

                    {!isPaidPlan ? (
                      <div className="flex justify-center">
                        <Link to="/plans">
                          <Button variant="gradient" size="sm">
                            {t('sidebar.upgradeToPro')}
                          </Button>
                        </Link>
                      </div>
                    ) : !isPastDue ? (
                      <div className="flex justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground"
                          onClick={() => setIsBuyExtraMinutesOpen(true)}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          {t('sidebar.buyMoreMinutes')}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  </div>
                </SidebarMenuItem>
                {(isPastDue || earlyRenewalPath === 'pix' || (subscriptionData?.payment_provider === 'pagarme' && subscriptionData?.pagarme_last_order_id)) && (
                  <SidebarMenuItem className="mt-2 px-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className={`w-full ${
                        isPastDue
                          ? 'border-red-600 text-red-600 hover:bg-red-600 hover:text-white'
                          : 'border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                      }`}
                      onClick={handlePastDuePay}
                      disabled={payingPastDue}
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      {isPastDue ? t('sidebar.payNow') : t('sidebar.renewPix')}
                    </Button>
                  </SidebarMenuItem>
                )}
                {renewedCyclePending && (
                  // Renewal already paid early: start it instead of paying again.
                  <SidebarMenuItem className="mt-2 px-2">
                    <RenewedCycleNotice
                      subscription={subscriptionData}
                      onStarted={refetchMyData}
                      variant="compact"
                      className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    />
                  </SidebarMenuItem>
                )}
                {earlyRenewalPath === 'grant' && (
                  <SidebarMenuItem className="mt-2 px-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      onClick={() => {
                        setRenewalModalVariant('grant');
                        setIsPixRenewalModalOpen(true);
                      }}
                    >
                      <Wallet className="w-4 h-4 mr-2" />
                      {t('account.renewPlanNow')}
                    </Button>
                  </SidebarMenuItem>
                )}
                  </>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {!collapsed && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarWalletCard />
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Bottom */}
        <div className={`mt-auto ${collapsed ? 'p-2 flex flex-col justify-center gap-2' : 'p-3 sm:p-4 space-y-3'}`}>
          <div className="border-t border-border pt-2">
            <Button
              variant="ghost"
              size="lg"
              className={`w-full h-8 flex justify-center text-muted-foreground ${collapsed ? 'p-0' : ''}`}
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
              {!collapsed && <span className="ml-2 text-sm sm:text-base">{t('sidebar.logout')}</span>}
            </Button>
          </div>
        </div>
      </SidebarContent>

      {/* Toggle Button */}
      <SidebarTrigger className="absolute -right-3 top-8 bg-card border border-border rounded-full p-1 shadow-md hover:bg-primary/90 hover:text-black" />

      {/* Buy Extra Minutes Modal */}
      <BuyExtraMinutesModal
        open={isBuyExtraMinutesOpen}
        onOpenChange={setIsBuyExtraMinutesOpen}
      />

      {/* PIX Renewal Modal */}
      <PixRenewalModal
        open={isPixRenewalModalOpen}
        onOpenChange={setIsPixRenewalModalOpen}
        onSuccess={async () => {
          await refetchMyData();
        }}
        autoGenerate={renewalModalVariant === 'pix'}
        variant={renewalModalVariant}
        resetNow={renewalModalVariant === 'pix' && renewalResetNow}
        onCouponApplied={refetchMyData}
      />
    </SidebarPrimitive>
  );
};

export default Sidebar;
